import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Trash2, Play, RefreshCw, Users, FileText, 
  Clock, CheckCircle, Edit3, Copy, X, Upload, Loader2,
  Link, ExternalLink, Zap, Square, RotateCcw, CopyPlus, Share2,
  BookTemplate, LayoutTemplate, Bookmark
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = window.location.origin;

export default function QuizManager({ onPresent, onEdit }) {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', mode: 'admin_controlled' });
  const [creating, setCreating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => { loadQuizzes(); }, []);

  const loadQuizzes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quiz/list`);
      setQuizzes(res.data.quizzes);
    } catch (err) {
      console.error('Failed to load quizzes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.title.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/quiz/create`, createForm);
      setShowCreate(false);
      setCreateForm({ title: '', description: '', mode: 'admin_controlled' });
      if (onEdit) onEdit(res.data.id);
      else loadQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create quiz');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quiz and all its data?')) return;
    try {
      await axios.delete(`${API}/quiz/${id}`);
      loadQuizzes();
    } catch (err) {
      alert('Failed to delete quiz');
    }
  };

  const goLive = async (id) => {
    try {
      await axios.post(`${API}/quiz/${id}/start`);
      loadQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start quiz');
    }
  };

  const endQuiz = async (id) => {
    try {
      await axios.post(`${API}/quiz/${id}/end`);
      loadQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to end quiz');
    }
  };

  const republish = async (id) => {
    try {
      await axios.post(`${API}/quiz/${id}/reset`);
      await axios.post(`${API}/quiz/${id}/start`);
      loadQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to republish');
    }
  };

  const duplicateQuiz = async (id) => {
    try {
      const res = await axios.post(`${API}/quiz/${id}/duplicate`);
      loadQuizzes();
      if (onEdit) onEdit(res.data.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to duplicate');
    }
  };

  const copyJoinLink = (code) => {
    const link = `${APP_URL}/quiz/join/${code}`;
    navigator.clipboard.writeText(link);
    alert('Participant join link copied!');
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Join code copied!');
  };

  const copyPresenterLink = async (quizId) => {
    try {
      const res = await axios.get(`${API}/quiz/${quizId}/presenter-link`);
      const link = `${APP_URL}/quiz/present/${quizId}?token=${res.data.presenter_token}`;
      navigator.clipboard.writeText(link);
      alert('Presenter link copied! Anyone with this link can control the quiz.');
    } catch (err) {
      alert('Failed to get presenter link');
    }
  };

  const saveAsTemplate = async (quizId) => {
    try {
      const res = await axios.post(`${API}/quiz/${quizId}/save-as-template`, {});
      alert(`Template saved! (${res.data.question_count} questions)`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/quiz/templates/list`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const createFromTemplate = async (templateId) => {
    try {
      const res = await axios.post(`${API}/quiz/templates/${templateId}/use`, {});
      if (onEdit) onEdit(res.data.id);
      else loadQuizzes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create from template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/quiz/templates/${templateId}`);
      loadTemplates();
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600',
      live: 'bg-green-100 text-green-700',
      ended: 'bg-red-100 text-red-600',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>{status}</span>;
  };

  return (
    <div data-testid="quiz-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1B3A6B]">Interactive Quizzes</h2>
          <p className="text-xs sm:text-sm text-[#475569] mt-1">Create live quizzes for your congregation</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowTemplates(!showTemplates); if (!showTemplates) loadTemplates(); }} className={`py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm border transition-colors ${showTemplates ? 'bg-violet-50 border-violet-300 text-violet-700' : 'border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`} data-testid="toggle-templates">
            <LayoutTemplate className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="create-quiz-btn">
            <Plus className="w-4 h-4" /> New Quiz
          </button>
          <button onClick={loadQuizzes} className="p-2 rounded-lg border border-[#E2DFD8] hover:bg-[#F4F2EC] transition-colors">
            <RefreshCw className="w-4 h-4 text-[#475569]" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#475569]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading quizzes...
        </div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#475569] mb-4">No quizzes yet. Create your first interactive quiz!</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-6 rounded-lg font-medium text-sm">
            Create Quiz
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {quizzes.map(q => (
            <div key={q.id} className="bg-white rounded-xl shadow-sm p-4 sm:p-5 border border-[#E2DFD8] hover:shadow-md transition-shadow" data-testid={`quiz-card-${q.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-[#0F1A2A] text-base sm:text-lg leading-tight pr-2">{q.title}</h3>
                {statusBadge(q.status)}
              </div>
              {q.description && <p className="text-xs sm:text-sm text-[#475569] mb-2 line-clamp-2">{q.description}</p>}
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-[#94A3B8] mb-3">
                <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{q.question_count}q</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{q.participant_count}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{q.mode === 'admin_controlled' ? 'Live' : 'Self-paced'}</span>
              </div>

              {/* Participant Join Link */}
              <div className="bg-[#F9F8F6] rounded-lg p-2 sm:p-3 mb-3 border border-[#E2DFD8]">
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] sm:text-xs text-[#1B3A6B] bg-white px-2 py-1 rounded border border-[#E2DFD8] truncate min-w-0" data-testid={`join-link-${q.id}`}>
                    .../quiz/join/{q.join_code}
                  </code>
                  <button onClick={() => copyJoinLink(q.join_code)} className="p-1.5 bg-[#1B3A6B] text-white rounded-md hover:bg-[#254B8A] transition-colors flex-shrink-0" title="Copy join link" data-testid={`copy-link-${q.id}`}>
                    <Link className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-[#94A3B8]">Code:</span>
                  <button onClick={() => copyCode(q.join_code)} className="flex items-center gap-1 text-xs font-mono font-bold text-[#1B3A6B] hover:underline" data-testid={`copy-code-${q.id}`}>
                    {q.join_code} <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-1.5">
                {q.status === 'draft' && q.question_count > 0 && (
                  <button onClick={() => goLive(q.id)} className="flex-1 min-w-[80px] py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-colors" data-testid={`publish-quiz-${q.id}`}>
                    <Zap className="w-3.5 h-3.5" /> Go Live
                  </button>
                )}
                {q.status === 'live' && (
                  <>
                    <button onClick={() => onPresent && onPresent(q.id)} className="flex-1 min-w-[80px] py-2 bg-[#1B3A6B] hover:bg-[#254B8A] text-white rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-colors" data-testid={`present-quiz-${q.id}`}>
                      <Play className="w-3.5 h-3.5" /> Present
                    </button>
                    <button onClick={() => endQuiz(q.id)} className="py-2 px-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors" title="End quiz" data-testid={`end-quiz-${q.id}`}>
                      <Square className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                {q.status === 'ended' && q.question_count > 0 && (
                  <>
                    <button onClick={() => republish(q.id)} className="flex-1 min-w-[80px] py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-colors" data-testid={`republish-quiz-${q.id}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Republish
                    </button>
                    <button onClick={() => onPresent && onPresent(q.id)} className="py-2 px-2.5 bg-[#475569] hover:bg-[#374151] text-white rounded-lg transition-colors" title="View results" data-testid={`view-results-${q.id}`}>
                      <Users className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                <button onClick={() => duplicateQuiz(q.id)} className="py-2 px-2 hover:bg-blue-50 rounded-lg transition-colors border border-[#E2DFD8]" title="Copy" data-testid={`duplicate-quiz-${q.id}`}>
                  <CopyPlus className="w-3.5 h-3.5 text-blue-600" />
                </button>
                <button onClick={() => copyPresenterLink(q.id)} className="py-2 px-2 hover:bg-violet-50 rounded-lg transition-colors border border-[#E2DFD8]" title="Share presenter link" data-testid={`share-presenter-${q.id}`}>
                  <Share2 className="w-3.5 h-3.5 text-violet-600" />
                </button>
                {q.question_count > 0 && (
                  <button onClick={() => saveAsTemplate(q.id)} className="py-2 px-2 hover:bg-amber-50 rounded-lg transition-colors border border-[#E2DFD8]" title="Save as template" data-testid={`save-template-${q.id}`}>
                    <Bookmark className="w-3.5 h-3.5 text-amber-600" />
                  </button>
                )}
                <button onClick={() => onEdit && onEdit(q.id)} className="py-2 px-2 hover:bg-[#F4F2EC] rounded-lg transition-colors border border-[#E2DFD8]" title="Edit" data-testid={`edit-quiz-${q.id}`}>
                  <Edit3 className="w-3.5 h-3.5 text-[#475569]" />
                </button>
                <button onClick={() => handleDelete(q.id)} className="py-2 px-2 hover:bg-red-50 rounded-lg transition-colors border border-[#E2DFD8]" title="Delete" data-testid={`delete-quiz-${q.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Templates Panel */}
      {showTemplates && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-5" data-testid="templates-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#0F1A2A] flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-violet-600" /> Quiz Templates
            </h3>
            <button onClick={() => setShowTemplates(false)} className="p-1 hover:bg-[#F4F2EC] rounded"><X className="w-4 h-4 text-[#475569]" /></button>
          </div>
          {loadingTemplates ? (
            <div className="text-center py-8 text-[#475569]"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" /> Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <LayoutTemplate className="w-10 h-10 text-[#94A3B8] mx-auto mb-2 opacity-40" />
              <p className="text-sm text-[#475569]">No templates yet. Save a quiz as a template using the <Bookmark className="w-3.5 h-3.5 inline text-amber-600" /> button.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map(t => (
                <div key={t.id} className="border border-[#E2DFD8] rounded-xl p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-violet-50/50 to-white" data-testid={`template-card-${t.id}`}>
                  <h4 className="font-semibold text-[#0F1A2A] text-sm mb-1 truncate">{t.title}</h4>
                  {t.description && <p className="text-xs text-[#475569] mb-2 line-clamp-1">{t.description}</p>}
                  <div className="flex items-center gap-3 text-[10px] text-[#94A3B8] mb-3">
                    <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{t.question_count}q</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.mode === 'admin_controlled' ? 'Live' : 'Self-paced'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => createFromTemplate(t.id)} className="flex-1 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors" data-testid={`use-template-${t.id}`}>
                      <Plus className="w-3 h-3" /> Use Template
                    </button>
                    <button onClick={() => deleteTemplate(t.id)} className="py-1.5 px-2 hover:bg-red-50 rounded-lg transition-colors border border-[#E2DFD8]" title="Delete template" data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Quiz Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="create-quiz-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0F1A2A]">Create New Quiz</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1">Title</label>
                <input type="text" value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})} placeholder="e.g., Sunday Bible Trivia" className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent" data-testid="quiz-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1">Description (optional)</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder="Brief description..." rows={2} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1">Mode</label>
                <select value={createForm.mode} onChange={e => setCreateForm({...createForm, mode: e.target.value})} className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]" data-testid="quiz-mode-select">
                  <option value="admin_controlled">Live (Admin controls pace)</option>
                  <option value="self_paced">Self-paced (Participants go at own speed)</option>
                </select>
              </div>
              <button onClick={handleCreate} disabled={creating || !createForm.title.trim()} className="w-full btn-primary py-2.5 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-quiz-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {creating ? 'Creating...' : 'Create Quiz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
